import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import Typhography from "../../shared/Typhography/Typhography";
import CustomButton from "../../shared/CustomButton/CustomButton";
import CustomTabsV3 from "../../shared/CustomTabsV3/CustomTabsV3";
import {
  AvatarGroup,
  Box,
  Button,
  Menu,
  Portal,
  Tabs,
  Tooltip,
} from "@chakra-ui/react";
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import dayjs from "dayjs";
import itLocale from "@fullcalendar/core/locales/it";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  DotsThreeVerticalIcon,
  MapPinLineIcon,
} from "@phosphor-icons/react";
import cx from "classnames";
import listPlugin from "@fullcalendar/list";
import style from "./CalendarPage.module.scss";
import { CustomBadge } from "../../shared/CustomBadge/CustomBadge";
import AddEventModal, {
  IEVentForm,
} from "../../components/CalendarPage/AddEventModal/AddEventModal";
import RecurrenceScopeDialog from "../../components/CalendarPage/RecurrenceScopeDialog/RecurrenceScopeDialog";
import { EVENT_TYPES } from "../../enums/event";
import { ITeam } from "../../models/Team";
import {
  buildEventBody,
  createEventsFromForm,
  isEventFormInvalid,
} from "../../components/CalendarPage/AddEventModal/eventForm.logic";
import { useEndpoints } from "../../hooks/useEndpoints";
import { IEvent } from "../../models/event";
import interactionPlugin from "@fullcalendar/interaction"; // NECESSARIO per interazione
import { ShowByRole } from "../../shared/ShowByRole";
import { customPlugin } from "../../components/CalendarPage/Plugin/CusromFlexListView";
import CustomAvatar from "../../shared/CustomAvatar/CustomAvatar";
import {
  addBaseUrl,
  getLabelFromOptions,
} from "../../lib/DurationPicker/utils";
import { formatFullName } from "../../utils/functions";
import { EndpointWithPagination } from "../../typings/Pagination";
import { useDebounce } from "../../hooks/useDebounce";
import { EVENT_TYPES_OPTIONS, LEVELS_SPORT_OPTIONS } from "../../constants/config";
import SlotCounter from "react-slot-counter";
import toast from "react-hot-toast";
import { CompileEventModal } from "../../components/CalendarPage/CompileEventModal/CompileEventModal";
import WorkoutDetailModal from "../../components/ProgrammingPage/WorkoutDetailModal/WorkoutDetailModal";
import OccurrenceDetailModal from "../../components/ProgrammingPage/OccurrenceDetailModal/OccurrenceDetailModal";
import WorkoutPlanCompileModal from '../../components/ProgrammingPage/WorkoutPlanCompileModal/WorkoutPlanCompileModal';
import WorkoutBuilderModal from '../../components/ProgrammingPage/WorkoutBuilderModal/WorkoutBuilderModal';
import AssignWorkoutModal from '../../components/ProgrammingPage/AssignWorkoutModal/AssignWorkoutModal';
import { IWorkout } from '../../models/programming';
import { AuthContext } from "../../providers/authProvider";
import { useAppSelector } from "../../store";
import CustomSelectInput from "../../shared/CustomSelectInput/CustomSelectInput";
import { TypeSpaceIcon } from "../../shared/Icons/Icons";
import { useConfirmModal } from "../../hooks/useConfirmModal/useConfirmModal";

interface ICalendarPage {}
function getMissingDateRanges(
  inputStart: Date,
  inputEnd: Date,
  knownRange: {
    startDate: Date;
    endDate: Date;
  },
): {
  missingStartRange?: { from: Date; to: Date };
  missingEndRange?: { from: Date; to: Date };
} {
  const result: {
    missingStartRange?: { from: Date; to: Date };
    missingEndRange?: { from: Date; to: Date };
  } = {};

  const start = dayjs(inputStart).startOf("day");
  const end = dayjs(inputEnd).startOf("day");
  const rangeStart = dayjs(knownRange.startDate).startOf("day");
  const rangeEnd = dayjs(knownRange.endDate).startOf("day");

  if (start.isBefore(rangeStart)) {
    result.missingStartRange = {
      from: start.startOf("day").toDate(),
      to: rangeStart.subtract(1, "day").endOf("day").toDate(),
    };
  }

  if (end.isAfter(rangeEnd)) {
    result.missingEndRange = {
      from: rangeEnd.add(1, "day").startOf("day").toDate(),
      to: end.endOf("day").toDate(),
    };
  }

  return result;
}
const CalendarPage: FC<ICalendarPage> = () => {
  const [currentTab, setCurrentTab] = useState("dayGridMonth");
  const calendarRef = useRef<FullCalendar>(null);
  const queryClient = useQueryClient();
  const {
    postEvent,
    patchEvent,
    getEvents,
    getEvent,
    deleteEvent,
    patchEventOccurrence,
    deleteAssignmentOccurrence,
    getTeams,
    duplicateProgrammingWorkout,
  } = useEndpoints();
  const { isCoach, isAdmin, isDirector } = useContext(AuthContext);
  const confirmModal = useConfirmModal();
  const selectedEntity = useAppSelector((st) => st.me.selectedEntity);
  const [availableTeams, setAvailableTeams] = useState<ITeam[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [events, setEvents] = useState<EndpointWithPagination<IEvent>>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isOpen, setIsOpen] = useState<IEvent | string>();
  const [isOpenCompileEvent, setIsOpenCompileEvent] = useState<IEvent>();
  const [viewWorkoutId, setViewWorkoutId] = useState<string | null>(null);
  const [scopeDialog, setScopeDialog] = useState<{
    event: IEvent;
    action: "edit" | "delete";
  } | null>(null);
  const [recurringEditScope, setRecurringEditScope] = useState<
    "this" | "this_and_following" | "all" | null
  >(null);
  const [occurrenceDetail, setOccurrenceDetail] = useState<{
    eventId: string;
    workoutAssignmentId: string;
  } | null>(null);
  const [occurrenceEditMode, setOccurrenceEditMode] = useState<{
    assignmentId: string;
    eventId: string;
    scope: 'single' | 'this_and_future' | 'all';
    workoutData: any;
  } | null>(null);
  const [compileModalData, setCompileModalData] = useState<{
    assignmentId: string;
    eventId: string;
    workout: any;
    athletes: any[];
    preSelectedAthleteId?: string;
  } | null>(null);
  const [newWorkoutFlowDate, setNewWorkoutFlowDate] = useState<Date | null>(null);
  const [newWorkoutFlowWorkout, setNewWorkoutFlowWorkout] = useState<IWorkout | null>(null);
  const rangeDate = useRef<{ startDate: Date; endDate: Date }>();
  const { callbackDebounce } = useDebounce();

  useEffect(() => {
    if (isCoach) {
      const teams = (selectedEntity as any)?.teams;
      setAvailableTeams(Array.isArray(teams) ? teams : teams ? [teams] : []);
    } else if (isAdmin || isDirector) {
      getTeams({ limit: 200, populate: "spaceId" }).then(({ data }) => setAvailableTeams(data.docs ?? []));
    }
  }, [isCoach, isAdmin, isDirector, selectedEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (rangeDate.current) {
      rangeDate.current = undefined;
      if (calendarRef.current) {
        // getCurrentData() è un'API interna di FullCalendar non esposta nei tipi pubblici
        const api = calendarRef.current.getApi() as unknown as {
          getCurrentData: () => { dateProfile: { activeRange: { start: Date; end: Date } } };
        };
        const { start, end } = api.getCurrentData().dateProfile.activeRange;
        fetch(start, end);
      }
    }
  }, [selectedTeamIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const isRecurringEvent = (event: IEvent) =>
    !!(event as any).recurringEventId ||
    !!(event as any).recurrence?.rrule;

  /** Evento allenamento con scheda strutturata assegnata */
  const isWorkoutScheda = (event: IEvent | null | undefined) =>
    !!(event as any)?.workoutId;

  const refetch = () => {
    if (rangeDate.current) {
      fetch(rangeDate.current.startDate, rangeDate.current.endDate);
    }
  };

  const handleDeleteEvent = async (event: IEvent) => {
    const id = toast.loading("Eliminazione in corso...");
    try {
      await deleteEvent(event._id);
      toast.success("Evento eliminato", { id });
      refetch();
    } catch {
      toast.error("Errore eliminazione", { id });
    }
  };

  // aggiornare il title al montaggio e quando serve
  // const {
  //   data: events,
  //   isLoading,
  //   searchValue,
  //   params,
  //   updateParam,
  //   updateSearchValue,
  //   fetch,
  // } = useAsyncTable("getEvents", undefined, {
  //   "startDate>": rangeDate.current.start,
  //   "endDate<": rangeDate.current.endDate,
  //   populate:
  //     "teams.image,teams.name,teams:spaceId.type,users.firstName,users.lastName,users.email,users:image.image",
  // });

  const fetch = async (start: Date, end: Date) => {
    try {
      setIsLoading(true);
      const { data } = await getEvents({
        start: { from: start, to: end },
        populate:
          "teams.image,teams.name,teams.level,teams:spaceId.type,users.firstName,users.lastName,users.email,users:image.image,testId",
        ...(selectedTeamIds.length > 0 ? { teams: selectedTeamIds } : {}),
      });
      setEvents(data);
      rangeDate.current = {
        startDate: start,
        endDate: end,
      };
    } catch {
      toast.error("Errore caricamento calendario");
    } finally {
      setIsLoading(false);
    }
  };

  const updateEvents = async (start: Date, end: Date) =>
    callbackDebounce(
      async () => {
        if (!rangeDate.current) {
          fetch(start, end);
          return;
        }
        const result = getMissingDateRanges(start, end, rangeDate.current);
        setIsLoading(true);

        try {
          if (result.missingStartRange || result.missingEndRange) {
            const $or = [];
            const filter: any = {};
            if (result.missingStartRange) {
              filter["start"] = {
                from: result.missingStartRange.from,
                to: result.missingStartRange.to,
              };
              $or.push({
                startDate: {
                  $gte: result.missingStartRange.from,
                  $lte: result.missingStartRange.to,
                },
                endDate: {
                  $gte: result.missingStartRange.from,
                  $lte: result.missingStartRange.to,
                },
              });
            }
            if (result.missingEndRange) {
              filter["end"] = {
                from: result.missingEndRange.from,
                to: result.missingEndRange.to,
              };
              $or.push({
                startDate: {
                  $gte: result.missingEndRange.from,
                  $lte: result.missingEndRange.to,
                },
                endDate: {
                  $gte: result.missingEndRange.from,
                  $lte: result.missingEndRange.to,
                },
              });
            }

            const { data } = await getEvents({
              ...filter,
              populate:
                "teams.image,teams.name,teams:spaceId.type,users.firstName,users.lastName,users.email,users:image.image,testId",
            });
            setEvents((prev) => {
              if (!prev) return data;
              return { ...prev, docs: [...prev.docs, ...data.docs] };
            });
            if (result.missingStartRange) {
              rangeDate.current.startDate = result.missingStartRange.from;
            }
            if (result.missingEndRange) {
              rangeDate.current.endDate = result.missingEndRange.to;
            }
          }
        } catch {
          toast.error("Errore caricamento calendario");
        } finally {
          setIsLoading(false);
        }
        // try {
        //   setIsLoading(true);
        //   const { data } = await getEvents({
        //     "startDate>": rangeDate.current.start,
        //     "endDate<": rangeDate.current.endDate,
        //     populate:
        //       "teams.image,teams.name,teams:spaceId.type,users.firstName,users.lastName,users.email,users:image.image",
        //   });
        //   setEvents(data);
        // } catch (error) {
        //   console.log("error", error);
        // } finally {
        //   setIsLoading(false);
        // }
      },
      "fetch-events",
      80,
    );

  const currentEvents = useMemo(() => {
    if (!events?.docs) return [];
    return events?.docs.filter((event) =>
      dayjs(event.date || event.startDate).isSame(selectedDate, "day"),
    );
  }, [events?.docs, selectedDate]);

  const onClose = () => {
    setIsOpen(undefined);
  };
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      setTitle(calendarApi.view.title);
    }
  }, []);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(currentTab);
    }
  }, [currentTab]);
  return (
    <>
      {/* <BackgroundPage /> */}
      <div className={style.containerPage}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column gap-1">
            <Typhography type="h1" size="bold-24" className={style.title}>
              Calendario
            </Typhography>
            <Typhography size="regular-14" className={style.title}>
              Scopri tutti gli eventi e le attività passsate
            </Typhography>
          </div>
          {availableTeams.length > 0 && (
            <div className={style.teamSelectWrapper}>
              <CustomSelectInput
                isMulti
                placeholder="Tutte le squadre"
                value={selectedTeamIds}
                options={availableTeams.map((t) => ({ label: t.name, value: t._id, team: t }))}
                onChange={(val: string[]) => setSelectedTeamIds(val ?? [])}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                components={{
                  Option: ({ data, isSelected, isFocused, innerProps, innerRef }: any) => {
                    const team: ITeam<any> = data.team;
                    const initials = team?.name?.slice(0, 2).toUpperCase() ?? "??";
                    const imgUrl = team?.image ? addBaseUrl(1, team.image) : undefined;
                    const spaceType = (team?.spaceId as any)?.type as keyof typeof LEVELS_SPORT_OPTIONS | undefined;
                    const { style: _ignored, ...safeInnerProps } = innerProps;
                    return (
                      <div
                        ref={innerRef}
                        {...safeInnerProps}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "10px 12px", cursor: "pointer",
                          borderLeft: isSelected ? "3px solid #ffbf18" : "3px solid transparent",
                          borderBottom: "1px solid #e7e7e7",
                          backgroundColor: isSelected ? "#ffdc81" : isFocused ? "#f2f2f2" : "#ffffff",
                          transition: "background-color 0.12s ease",
                        }}
                      >
                        {/* Icon wrapper */}
                        <div style={{
                          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          background: imgUrl ? `url(${imgUrl}) center/cover no-repeat` : "#f6f6f6",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: "1px solid #e7e7e7",
                          color: "#4d4d4d",
                        }}>
                          {imgUrl
                            ? null
                            : spaceType
                              ? <TypeSpaceIcon type={spaceType} size={18} />
                              : <span style={{ fontSize: 11, fontWeight: 700 }}>{initials}</span>
                          }
                        </div>

                        {/* Nome + chip livello */}
                        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            fontSize: 14, fontWeight: 700, color: "#00212c",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {team?.name}
                          </span>
                          {team?.level && (
                            <span style={{
                              display: "inline-flex", alignItems: "center",
                              border: "1px solid #e7e7e7", borderRadius: 100,
                              padding: "2px 8px", fontSize: 12, color: "#4d4d4d",
                              background: "#f6f6f6", flexShrink: 0,
                            }}>
                              {getLabelFromOptions((spaceType ? LEVELS_SPORT_OPTIONS[spaceType] : undefined) ?? [], team.level) || team.level}
                            </span>
                          )}
                        </div>

                        {/* Check badge */}
                        {isSelected && (
                          <div style={{
                            width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                            backgroundColor: "#ffbf18",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, color: "#03212c", fontWeight: 900,
                          }}>✓</div>
                        )}
                      </div>
                    );
                  },
                }}
              />
            </div>
          )}
        </div>
        {/* {renderFilters()} */}
        <div className="d-flex gap-4 w-100 flex-1 overflow-hidden position-relative">
          {isLoading && (
            <Box
              className="common-loader"
              data-state="open"
              _open={{
                animation: "fade-in 100ms ease-out",
              }}
            >
              <LeopartLoader />
            </Box>
          )}
          <div className={style.containerLeft}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-1">
                <Typhography size="bold-24">
                  <SlotCounter value={+dayjs(selectedDate).format("DD")} />
                </Typhography>

                <Typhography size="bold-24">
                  {dayjs(selectedDate).format("MMMM YYYY")}
                </Typhography>
              </div>
              <CustomButton
                color="text"
                icon="plus"
                iconPosition="left"
                onClick={() => {
                  setIsOpen(selectedDate.toISOString());
                }}
              >
                Aggiungi evento
              </CustomButton>
            </div>
            <Typhography size="regular-16">
              {dayjs(selectedDate).format("dddd")}
            </Typhography>
            <div className="d-flex flex-column w-100 mt-3 gap-3 overflow-auto">
              {currentEvents.map((event) => {
                const startDate = dayjs(event.date || event.startDate);
                const endDate = dayjs(event.endDate);
                const hasWorkoutId = !!(event as any).workoutAssignmentId;
                const borderClass = (event as any).completed
                  ? style.containerEventCompleted
                  : isWorkoutScheda(event) || hasWorkoutId
                    ? style.containerEventScheda
                    : event.type === EVENT_TYPES.TEST || event.type === EVENT_TYPES.SYSTEM_TEST_EVENT
                      ? style.containerEventTest
                      : event.type === EVENT_TYPES.MATCH
                        ? style.containerEventMatch
                        : style.containerEventWorkout;
                return (
                  <Box
                    data-state="open"
                    className={cx(style.containerEvent, borderClass)}
                    key={event._id}
                    _open={{
                      animationName: "fade-in",
                      animationDuration: "100ms",
                    }}
                  >
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
                          <CustomBadge
                            label={
                              isWorkoutScheda(event)
                                ? "Scheda"
                                : getLabelFromOptions(EVENT_TYPES_OPTIONS, event.type)
                            }
                            color={
                              isWorkoutScheda(event)
                                ? "purple"
                                : event.type === EVENT_TYPES.WORKOUT
                                  ? "light-blue-dust"
                                  : event.type === EVENT_TYPES.TEST || event.type === EVENT_TYPES.SYSTEM_TEST_EVENT
                                    ? "lightgreen"
                                    : "lightblue"
                            }
                          />
                          {isRecurringEvent(event) && (
                            <div className={style.recurringBadge}>
                              <ArrowsClockwiseIcon size={12} weight="bold" />
                              <Typhography size="regular-12">
                                Ricorrente
                              </Typhography>
                            </div>
                          )}
                          {(event as any).completed && (
                            <div className={style.completedBadge}>
                              <CheckCircleIcon size={12} weight="fill" />
                              <Typhography size="regular-12">
                                Completato
                              </Typhography>
                            </div>
                          )}
                          {(event as any).workoutAssignmentId && (
                            <div
                              className={style.linkedBadge}
                              style={{ cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewWorkoutId((event as any).workoutId);
                              }}
                            >
                              <ClipboardTextIcon size={12} weight="bold" />
                              <Typhography size="regular-12">
                                Scheda
                              </Typhography>
                            </div>
                          )}
                          {isWorkoutScheda(event) && (event as any).dayNumber && (
                            <div className={style.recurringBadge} style={{ background: '#f0f4ff', color: '#4a7cff' }}>
                              <Typhography size="regular-12">
                                Giorno {(event as any).dayNumber}
                              </Typhography>
                            </div>
                          )}
                          <Typhography
                            size="bold-14"
                            title={(event as any)?.name || (event as any)?.testId?.name || (event as any)?.testType}
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}
                          >
                            {(event as any)?.name || (event as any)?.testId?.name || (event as any)?.testType}
                          </Typhography>
                        </div>
                        <ShowByRole roles={["isAdmin", "isDirector"]}>
                          <Menu.Root>
                            <Menu.Trigger as={DotsThreeVerticalIcon} asChild>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                as={DotsThreeVerticalIcon}
                                className={style.dotsContainer}
                              ></Button>
                            </Menu.Trigger>
                            <Portal>
                              <Menu.Positioner>
                                <Menu.Content className="ps-2 pe-2">
                                  <Menu.Item
                                    className="cursor-pointer"
                                    value="edit"
                                    onClick={() => {
                                      if (isWorkoutScheda(event) && (event as any).workoutAssignmentId) {
                                        setScopeDialog({
                                          event,
                                          action: "edit",
                                        });
                                      } else if (isRecurringEvent(event)) {
                                        setScopeDialog({
                                          event,
                                          action: "edit",
                                        });
                                      } else {
                                        setIsOpen(event);
                                      }
                                    }}
                                  >
                                    {isWorkoutScheda(event) ? 'Modifica Scheda' : 'Modifica Evento'}
                                  </Menu.Item>
                                  <Menu.Item
                                    className="cursor-pointer"
                                    value="compile"
                                    onClick={() => {
                                      setIsOpenCompileEvent(event);
                                    }}
                                  >
                                    Compila Evento
                                  </Menu.Item>
                                  {isWorkoutScheda(event) && (event as any).workoutAssignmentId && (
                                    <Menu.Item
                                      className="cursor-pointer"
                                      value="view-workout"
                                      onClick={() => {
                                        setOccurrenceDetail({
                                          eventId: event._id,
                                          workoutAssignmentId: (event as any).workoutAssignmentId,
                                        });
                                      }}
                                    >
                                      Dettaglio Scheda
                                    </Menu.Item>
                                  )}
                                  {isWorkoutScheda(event) && (
                                    <Menu.Item
                                      className="cursor-pointer"
                                      value="duplicate-workout"
                                      onClick={async () => {
                                        const id = toast.loading('Duplicazione in corso...');
                                        try {
                                          await duplicateProgrammingWorkout((event as any).workoutId);
                                          toast.success('Scheda duplicata nella libreria', { id });
                                          queryClient.invalidateQueries({ queryKey: ['programming-workouts'] });
                                        } catch {
                                          toast.error('Errore duplicazione', { id });
                                        }
                                      }}
                                    >
                                      Duplica Scheda
                                    </Menu.Item>
                                  )}
                                  <Menu.Item
                                    className="cursor-pointer"
                                    value="delete"
                                    onClick={() => {
                                      if (isWorkoutScheda(event) && (event as any).workoutAssignmentId) {
                                        setScopeDialog({
                                          event,
                                          action: "delete",
                                        });
                                      } else if (isRecurringEvent(event)) {
                                        setScopeDialog({
                                          event,
                                          action: "delete",
                                        });
                                      } else {
                                        confirmModal.handleOpen({
                                          title: "Elimina Evento",
                                          description:
                                            "Sei sicuro di voler eliminare questo evento? L'azione non può essere annullata.",
                                          onConfirm: () =>
                                            handleDeleteEvent(event),
                                        });
                                      }
                                    }}
                                  >
                                    Elimina Evento
                                  </Menu.Item>
                                </Menu.Content>
                              </Menu.Positioner>
                            </Portal>
                          </Menu.Root>
                        </ShowByRole>
                      </div>
                      {(event as any).compiledIds && (event as any).userIds && (event as any).userIds.length > 0 && (
                        <div className="d-flex align-items-center gap-1" style={{ marginTop: 2 }}>
                          <div style={{ flex: 1, height: 3, background: 'var(--c-card-border)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{
                              width: `${((event as any).compiledIds.length / Math.max((event as any).userIds.length, 1)) * 100}%`,
                              height: '100%',
                              background: (event as any).compiledIds.length === (event as any).userIds.length ? '#4dd292' : '#063444',
                              borderRadius: 2,
                            }} />
                          </div>
                          <span style={{ fontSize: 10, color: 'var(--c-text-3)' }}>
                            {(event as any).compiledIds.length}/{(event as any).userIds.length}
                          </span>
                        </div>
                      )}
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <Typhography size="medium-12">
                          {startDate.format("DD MMM YYYY")}
                        </Typhography>
                        {event.startDate && event.endDate && (
                          <Typhography size="regular-12">
                            {`${startDate.format("HH:mm")} - ${endDate.format(
                              "HH:mm",
                            )}`}
                          </Typhography>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-1">
                          <MapPinLineIcon className="color-text-2 " />
                          <Typhography
                            size="regular-12"
                            className="color-text-2"
                          >
                            {event.location || ""}
                          </Typhography>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          {(event as any).teams?.length > 0 && (
                            <div className="d-flex" style={{ gap: 0, marginLeft: 0 }}>
                              {(event as any).teams.slice(0, 3).map((team: any, idx: number) => {
                                const imgUrl = team.image ? addBaseUrl(1, team.image) : undefined;
                                const initials = team.name?.slice(0, 2).toUpperCase() ?? "??";
                                const spaceType = team.spaceId?.type;
                                return (
                                  <Tooltip.Root key={team._id} openDelay={100} closeDelay={0}>
                                    <Tooltip.Trigger asChild>
                                      <div
                                        style={{
                                          width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                                          background: imgUrl ? `url(${imgUrl}) center/cover no-repeat` : "#f0f0f0",
                                          display: "flex", alignItems: "center", justifyContent: "center",
                                          border: "1.5px solid #e0e0e0",
                                          marginLeft: idx === 0 ? 0 : -6,
                                          position: "relative", zIndex: 1,
                                          fontSize: 9, fontWeight: 700, color: "#4d4d4d",
                                        }}
                                      >
                                        {!imgUrl && (spaceType
                                          ? <TypeSpaceIcon type={spaceType} size={12} />
                                          : initials)}
                                      </div>
                                    </Tooltip.Trigger>
                                    <Portal>
                                      <Tooltip.Positioner>
                                        <Tooltip.Content>
                                          <Tooltip.Arrow>
                                            <Tooltip.ArrowTip />
                                          </Tooltip.Arrow>
                                          {team.name}
                                        </Tooltip.Content>
                                      </Tooltip.Positioner>
                                    </Portal>
                                  </Tooltip.Root>
                                );
                              })}
                              {(event as any).teams.length > 3 && (
                                <div style={{
                                  width: 24, height: 24, borderRadius: 6,
                                  background: "#ffbf18", display: "flex",
                                  alignItems: "center", justifyContent: "center",
                                  marginLeft: -6, position: "relative", zIndex: 1,
                                  fontSize: 9, fontWeight: 700, color: "#03212c",
                                  border: "1.5px solid #e0e0e0",
                                }}>
                                  +{(event as any).teams.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                          <AvatarGroup gap="0" spaceX="-3" size="2xs">
                            {event.users.slice(0, 3).map((user) => (
                              <Tooltip.Root key={user._id} openDelay={100} closeDelay={0}>
                                <Tooltip.Trigger asChild>
                                  <span>
                                    <CustomAvatar
                                      name={`${user.firstName[0]}${user.lastName[0]}`}
                                      src={addBaseUrl(1, user.image?.image)}
                                    />
                                  </span>
                                </Tooltip.Trigger>
                                <Portal>
                                  <Tooltip.Positioner>
                                    <Tooltip.Content>
                                      <Tooltip.Arrow>
                                        <Tooltip.ArrowTip />
                                      </Tooltip.Arrow>
                                      {formatFullName(user.firstName, user.lastName)}
                                    </Tooltip.Content>
                                  </Tooltip.Positioner>
                                </Portal>
                              </Tooltip.Root>
                            ))}

                            {event.users.slice(3).length > 0 && (
                              <CustomAvatar
                                theme="secondary"
                                name={`+${event.users.slice(3).length}`}
                              />
                            )}
                          </AvatarGroup>
                        </div>
                      </div>
                    </div>
                  </Box>
                );
              })}
            </div>
          </div>
          <div className={style.containerRight}>
            <CustomTabsV3
              lazyMount
              unmountOnExit
              classNameList={style.containerTabs}
              value={currentTab}
              onValueChange={(e) => setCurrentTab(e.value)}
              tabs={[
                {
                  label: "Mese",
                  value: "dayGridMonth",
                },
                {
                  label: "Settimana",
                  value: "custom",
                },
              ]}
            >
              <Tabs.Content value="dayGridMonth">
                <div className={style.containerToolbar}>
                  <div
                    className={style.containerButton}
                    onClick={() => calendarRef.current?.getApi().prev()}
                  >
                    <ArrowLeftIcon size={20} />
                  </div>
                  <Typhography size="bold-14">{title}</Typhography>
                  <div
                    className={style.containerButton}
                    onClick={() => calendarRef.current?.getApi().next()}
                  >
                    <ArrowRightIcon size={20} />
                  </div>
                </div>
              </Tabs.Content>

              <Tabs.Content value="custom">
                <div className={style.containerToolbar}>
                  <div
                    className={style.containerButton}
                    onClick={() => calendarRef.current?.getApi().prev()}
                  >
                    <ArrowLeftIcon size={20} />
                  </div>
                  <Typhography size="bold-14">{title}</Typhography>
                  <div
                    className={style.containerButton}
                    onClick={() => calendarRef.current?.getApi().next()}
                  >
                    <ArrowRightIcon size={20} />
                  </div>
                </div>
              </Tabs.Content>
            </CustomTabsV3>
            <div className="w-100 flex-1 overflow-hidden">
              <div className="overflow-auto w-100 h-100">
                <FullCalendar
                  ref={calendarRef}
                  plugins={[
                    dayGridPlugin,
                    listPlugin,
                    customPlugin,
                    interactionPlugin,
                  ]}
                  initialView={"custom"}
                  height={"auto"}
                  headerToolbar={false}
                  weekends={true}
                  dateClick={({ date }) => {
                    setSelectedDate(date);
                  }}
                  dayCellClassNames={(arg) => {
                    return dayjs(arg.date).format("YYYY-MM-DD") ===
                      dayjs(selectedDate).format("YYYY-MM-DD")
                      ? "selected-date"
                      : "";
                  }}
                  eventClick={({ event }) => {
                    if (event.start) setSelectedDate(event.start);
                  }}
                  locale={itLocale}
                  datesSet={(arg) => {
                    const api = calendarRef.current?.getApi();
                    updateEvents(arg.start, arg.end);
                    api && setTitle(api.view.title);
                  }}
                  eventClassNames={(arg) => {
                    const classes: string[] = [];
                    if (!!arg.event.extendedProps.workoutId) {
                      classes.push("event-workout-plan");
                    } else if (arg.event.extendedProps.type === EVENT_TYPES.WORKOUT) {
                      classes.push("event-workout");
                    } else if (
                      arg.event.extendedProps.type === EVENT_TYPES.TEST ||
                      arg.event.extendedProps.type === EVENT_TYPES.SYSTEM_TEST_EVENT
                    ) {
                      classes.push("event-test");
                    } else if (arg.event.extendedProps.type === EVENT_TYPES.MATCH) {
                      classes.push("event-match");
                    } else {
                      classes.push("event-workout");
                    }
                    if (arg.event.extendedProps.completed) {
                      classes.push("event-completed");
                    }
                    return classes;
                  }}
                  events={events?.docs.map((item) => {
                    return {
                      title: (item as any)?.name || (item as any)?.testId?.name || (item as any)?.testType,
                      date: dayjs(item.date || item.startDate).format(
                        "YYYY-MM-DD",
                      ),
                      extendedProps: {
                        id: item._id,
                        type: item.type,
                        completed: (item as any).completed,
                        workoutId: (item as any).workoutId,
                      },
                    };
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddEventModal
        isOpen={isOpen}
        isLoading={isConfirmLoading}
        recurringScope={recurringEditScope}
        onClose={onClose}
        onCreateNewWorkout={(date) => {
          setIsOpen(undefined);
          setNewWorkoutFlowDate(date);
        }}
        isInvalidForm={isEventFormInvalid}
        onConfirm={async (form: Partial<IEVentForm> & { dates?: string[] }) => {
          setIsConfirmLoading(true);
          try {
            const { body } = buildEventBody(form);

            const isAnyRecurring =
              isOpen &&
              typeof isOpen !== "string" &&
              isRecurringEvent(isOpen as IEvent);

            // Multi-date Test: spawna N postEvent in parallelo, uno per data.
            // Vale solo per CREATE (mai per edit di evento esistente).
            if (
              !isAnyRecurring &&
              !(isOpen && typeof isOpen !== "string")
            ) {
              await createEventsFromForm(form, postEvent);
            } else if (isAnyRecurring && recurringEditScope) {
              const ev = isOpen as any;
              if (recurringEditScope === "this") {
                await patchEvent(ev._id, body);
              } else {
                const masterId = ev.recurringEventId ?? ev._id;
                const originalStart = new Date(ev.startDate).toISOString();
                await patchEventOccurrence(
                  masterId,
                  originalStart,
                  recurringEditScope,
                  body,
                );
              }
              setRecurringEditScope(null);
            } else if (isOpen && typeof isOpen !== "string") {
              await patchEvent((isOpen as IEvent)._id, body);
            } else {
              await postEvent(body);
            }
            setIsOpen(undefined);
            refetch();
          } catch (error) {
            // rilancia: AddEventModal mappa i 400 di validazione sui campi RHF
            throw error;
          } finally {
            setIsConfirmLoading(false);
          }
        }}
      />
      <CompileEventModal
        isOpen={isOpenCompileEvent}
        onClose={() => {
          setIsOpenCompileEvent(undefined);
          refetch();
        }}
      />
      <WorkoutDetailModal
        workoutId={viewWorkoutId}
        onClose={() => setViewWorkoutId(null)}
      />
      <RecurrenceScopeDialog
        isOpen={!!scopeDialog}
        action={scopeDialog?.action ?? "edit"}
        onClose={() => setScopeDialog(null)}
        scopeMapping={
          isWorkoutScheda(scopeDialog?.event as IEvent)
            ? { this: 'single', this_and_following: 'this_and_future', all: 'all' }
            : undefined
        }
        onConfirm={async (scope) => {
          if (!scopeDialog) return;
          const ev = scopeDialog.event as any;
          const isWorkoutPlan = !!ev.workoutId;
          setScopeDialog(null);

          if (scopeDialog.action === "delete") {
            const id = toast.loading("Eliminazione in corso...");
            try {
              if (isWorkoutPlan) {
                const occScope = scope === "this_and_following" ? "this_and_future" : scope;
                await deleteAssignmentOccurrence(ev.workoutAssignmentId, ev._id, occScope as any);
              } else {
                const deleteScope = scope === "this_and_following" ? "this_and_future" : scope;
                await deleteEvent(ev._id, deleteScope);
              }
              toast.success("Eliminato", { id });
              refetch();
            } catch {
              toast.error("Errore eliminazione", { id });
            }
          } else {
            if (isWorkoutPlan) {
              setOccurrenceEditMode({
                assignmentId: ev.workoutAssignmentId,
                eventId: ev._id,
                scope: scope as 'single' | 'this_and_future' | 'all',
                workoutData: null,
              });
            } else {
              setRecurringEditScope(scope as "all" | "this" | "this_and_following");
              if (scope === "all" && ev.recurringEventId) {
                const { data: master } = await getEvent(ev.recurringEventId, {
                  populate:
                    "teams.image,teams.name,teams.level,teams:spaceId.type,users.firstName,users.lastName,users.email,users:image.image,testId",
                });
                setIsOpen(master);
              } else {
                setIsOpen(scopeDialog.event);
              }
            }
          }
        }}
      />
      <OccurrenceDetailModal
        isOpen={!!occurrenceDetail}
        onClose={() => setOccurrenceDetail(null)}
        eventId={occurrenceDetail?.eventId}
        workoutAssignmentId={occurrenceDetail?.workoutAssignmentId}
        onEdit={(event, workout) => {
          setOccurrenceDetail(null);
          setScopeDialog({ event, action: 'edit' });
        }}
        onDelete={(event) => {
          setOccurrenceDetail(null);
          setScopeDialog({ event, action: 'delete' });
        }}
        onCompile={(event) => {
          // Spec: "Compila" dentro Dettaglio Scheda deve avere lo stesso
          // comportamento di "Compila Evento" del menu 3-dots — modalità
          // compilazione a posteriori (RPE + durata), NON avvio sessione.
          // Avvio sessione è esclusivo dell'app atleta.
          setOccurrenceDetail(null);
          setIsOpenCompileEvent(event as IEvent);
        }}
      />
      <WorkoutPlanCompileModal
        isOpen={!!compileModalData}
        onClose={() => {
          setCompileModalData(null);
          refetch();
        }}
        assignmentId={compileModalData?.assignmentId ?? ''}
        eventId={compileModalData?.eventId}
        workout={compileModalData?.workout}
        preSelectedAthleteId={compileModalData?.preSelectedAthleteId}
        athletes={compileModalData?.athletes ?? []}
      />
      {newWorkoutFlowDate !== null && !newWorkoutFlowWorkout && (
        <WorkoutBuilderModal
          isOpen={true}
          onClose={() => setNewWorkoutFlowDate(null)}
          workout={null}
          onSaved={(workout) => {
            setNewWorkoutFlowWorkout(workout);
          }}
        />
      )}
      {/* Modifica scheda di una specifica occorrenza ricorrente: aperto da
          OccurrenceDetailModal "Modifica" → RecurrenceScopeDialog "Conferma".
          Senza questo render lo state viene settato ma niente UI compare. */}
      {occurrenceEditMode && (
        <WorkoutBuilderModal
          isOpen={true}
          onClose={() => setOccurrenceEditMode(null)}
          workout={null}
          occurrenceMode={occurrenceEditMode}
          onSaved={() => {
            setOccurrenceEditMode(null);
            refetch();
          }}
        />
      )}
      {newWorkoutFlowWorkout && (
        <AssignWorkoutModal
          isOpen={true}
          workout={newWorkoutFlowWorkout}
          preSelectedDate={newWorkoutFlowDate}
          onClose={() => {
            setNewWorkoutFlowWorkout(null);
            setNewWorkoutFlowDate(null);
            refetch();
          }}
        />
      )}
    </>
  );
};

export default CalendarPage;
